import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function Sidebar({
  isDark,
  setIsDark,
  activePage,
  entities = [],
  selectedEntity = null,
  onSelectEntity,
}) {
  const navigate = useNavigate();
  const [entityOpen, setEntityOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const popRef = useRef(null);

  const sidebar = isDark
    ? 'bg-slate-900/50 border-slate-800'
    : 'bg-slate-100/50 border-slate-200';
  const navLink = isDark
    ? 'text-slate-400 hover:text-slate-100'
    : 'text-slate-500 hover:text-slate-900';
  const navActive = isDark ? 'text-slate-100' : 'text-slate-900';
  const pillCls =
    'font-mono text-[11px] px-3 py-1 rounded-full border border-slate-500/60 bg-black/10 hover:bg-black/20 hover:border-slate-400/80 transition-colors w-full text-left';

  const pages = [
    { to: '/enheter', label: 'Enheter' },
    { to: '/kontant-avstemming', label: 'Kontantavstemming' },
  ];

  const activeEntity = entities.find((e) => e.id === selectedEntity);

  useEffect(() => {
    function onClickOutside(e) {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setEntityOpen(false);
      }
    }
    if (entityOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [entityOpen]);

  // Close mobile sidebar on ESC
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(e) {
      if (e.key === 'Escape') setMobileOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  function handleNavClick() {
    setMobileOpen(false);
  }

  const sidebarContent = (
    <>
      <div className="p-6">
        <h1 className="font-mono text-sm tracking-tight">kontant.backstube.no</h1>
      </div>

      <nav className="flex-1 px-4 space-y-1">
        {pages.map((p) => (
          <Link
            key={p.to}
            to={p.to}
            onClick={handleNavClick}
            className={`block font-mono text-xs px-3 py-2 rounded transition-colors ${
              activePage === p.to ? navActive : navLink
            }`}
          >
            {p.label}
          </Link>
        ))}
      </nav>

      <div className="p-4 space-y-2">
        {/* Entity switcher */}
        {entities.length > 0 && (
          <div className="relative" ref={popRef}>
            {entities.length > 1 ? (
              <button
                type="button"
                onClick={() => setEntityOpen((v) => !v)}
                className={`${pillCls} flex items-center justify-between gap-1`}
              >
                <span className="truncate">
                  {activeEntity?.name ?? entities[0]?.name}
                </span>
                <svg
                  className={`w-3 h-3 shrink-0 transition-transform ${entityOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            ) : (
              <p className={`font-mono text-[11px] px-3 py-1 truncate ${
                isDark ? 'text-slate-500' : 'text-slate-400'
              }`}>
                {entities[0]?.name}
              </p>
            )}

            {entityOpen && entities.length > 1 && (
              <div
                className={`absolute bottom-full left-0 right-0 mb-1 rounded-lg border shadow-lg overflow-hidden ${
                  isDark
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-slate-200'
                }`}
              >
                {entities.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => {
                      onSelectEntity?.(e.id);
                      setEntityOpen(false);
                    }}
                    className={`block w-full text-left font-mono text-[11px] px-3 py-1.5 transition-colors ${
                      e.id === selectedEntity
                        ? isDark
                          ? 'bg-slate-700 text-slate-100'
                          : 'bg-slate-100 text-slate-900'
                        : isDark
                          ? 'text-slate-300 hover:bg-slate-700/60'
                          : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {e.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button type="button" onClick={handleLogout} className={pillCls}>
          Logg ut
        </button>

        <button
          type="button"
          onClick={() => setIsDark((prev) => !prev)}
          className={pillCls}
        >
          {isDark ? 'Fiat lux' : 'Fiat nox'}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Hamburger button — mobile only */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className={`fixed top-4 left-4 z-50 md:hidden p-2 rounded-lg border transition-colors ${
          isDark
            ? 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800'
            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
        }`}
        aria-label="Åpne meny"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Backdrop — mobile only */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — fixed overlay on mobile, static on md+ */}
      <aside
        className={[
          'w-48 flex flex-col border-r shrink-0 h-screen',
          sidebar,
          // Desktop: static in layout
          'hidden md:flex md:sticky md:top-0',
          // Mobile: shown only when mobileOpen
        ].join(' ')}
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar drawer */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 w-48 flex flex-col border-r md:hidden',
          sidebar,
          'transition-transform duration-200 ease-in-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Close button inside drawer */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className={`absolute top-4 right-3 p-1 rounded transition-colors ${
            isDark ? 'text-slate-400 hover:text-slate-100' : 'text-slate-500 hover:text-slate-900'
          }`}
          aria-label="Lukk meny"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {sidebarContent}
      </aside>
    </>
  );
}
